import {z} from 'zod';

import i18n from '@/i18n';
import {graphqlClient} from '@/services/graphqlClient';
import type {ResidentExportFragmentFragment} from '@/services/graphqlHooks';
import {
  useGetCurrentResidentsForExportQuery,
  useGetOldResidentsForExportQuery,
  useGetPendingResidentsForExportQuery,
} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';
import type {ResidentGroup} from '@/translation/residentGroups';
import residentGroups from '@/translation/residentGroups';
import residentStatuses from '@/translation/residentStatuses';

import ResidentGroupOptionsFields from '../../components/export-options/ResidentGroupOptionsFields';
import type {ExportDefinition} from '../../types/exportTypes';
import {formatExportDate} from '../exportOptions';
import {EXPORT_RESIDENT_GROUP_PARAM} from '../exportParams';

export const RESIDENT_GROUPS = [
  'activated',
  'pending',
  'deactivated',
] as const satisfies ResidentGroup[];

const residentsOptionsSchema = z.object({
  group: z.enum(RESIDENT_GROUPS),
});

export type ResidentsOptions = z.infer<typeof residentsOptionsSchema>;

type ResidentsWithRooms = {
  residents: ResidentExportFragmentFragment[];
  rooms: {id: string; roomNumber: string}[];
};

const residentLoaders: Record<
  ResidentGroup,
  () => Promise<ResidentsWithRooms>
> = {
  activated: async () => {
    const {getAllCurrentResidents, getRooms} = await queryClient.fetchQuery({
      queryKey: useGetCurrentResidentsForExportQuery.getKey(),
      queryFn: useGetCurrentResidentsForExportQuery.fetcher(graphqlClient),
    });

    return {residents: getAllCurrentResidents, rooms: getRooms};
  },
  pending: async () => {
    const {getAllPendingResidents, getRooms} = await queryClient.fetchQuery({
      queryKey: useGetPendingResidentsForExportQuery.getKey(),
      queryFn: useGetPendingResidentsForExportQuery.fetcher(graphqlClient),
    });

    return {residents: getAllPendingResidents, rooms: getRooms};
  },
  deactivated: async () => {
    const {getAllOldResidents, getRooms} = await queryClient.fetchQuery({
      queryKey: useGetOldResidentsForExportQuery.getKey(),
      queryFn: useGetOldResidentsForExportQuery.fetcher(graphqlClient),
    });

    return {residents: getAllOldResidents, rooms: getRooms};
  },
};

const isResidentGroup = (value: string | null): value is ResidentGroup =>
  RESIDENT_GROUPS.some(group => group === value);

type ResidentExportRecord = ResidentExportFragmentFragment & {
  roomNumber?: string;
};

export const residentsExport: ExportDefinition<
  ResidentsOptions,
  ResidentExportRecord
> = {
  id: 'residents',
  label: i18n.t('Residents'),
  permission: 'EXPORT_RESIDENTS',
  entitlement: 'RESIDENTS',
  optionsSchema: residentsOptionsSchema,
  OptionsFields: ResidentGroupOptionsFields,
  getDefaultOptions: searchParams => {
    const group = searchParams.get(EXPORT_RESIDENT_GROUP_PARAM);

    return {group: isResidentGroup(group) ? group : 'activated'};
  },
  load: async ({group}) => {
    const {residents, rooms} = await residentLoaders[group]();
    const roomNumbers = new Map(rooms.map(room => [room.id, room.roomNumber]));

    return residents.map(resident => ({
      ...resident,
      roomNumber: resident.roomId
        ? roomNumbers.get(resident.roomId)
        : undefined,
    }));
  },
  describeOptions: ({group}) => residentGroups[group],
  columns: [
    {
      header: i18n.t('Personal number'),
      value: resident => resident.personalNumber,
    },
    {header: i18n.t('First name'), value: resident => resident.name.firstName},
    {
      header: i18n.t('Middle name'),
      value: resident => resident.name.middleName,
    },
    {header: i18n.t('Last name'), value: resident => resident.name.lastName},
    {
      header: i18n.t('Occupied room'),
      filterable: true,
      value: resident => resident.roomNumber,
    },
    {
      header: i18n.t('Date of birth'),
      value: resident => formatExportDate(resident.dateOfBirth),
    },
    {
      header: i18n.t('Accommodated since'),
      value: resident => formatExportDate(resident.accommodatedSince),
    },
    {
      header: i18n.t('Accommodated until'),
      value: resident => formatExportDate(resident.accommodatedUntil),
    },
    {
      header: i18n.t('Phone'),
      value: resident => resident.contactPersons[0]?.phoneNumber,
    },
    {
      header: i18n.t('Email'),
      value: resident => resident.contactPersons[0]?.email,
    },
    {
      header: i18n.t('Status'),
      filterable: true,
      defaultHidden: true,
      value: resident => residentStatuses[resident.status],
    },
  ],
  getFileName: ({group}) =>
    group === 'activated' ? 'residents' : `residents-${group}`,
};
